import { ref, computed } from 'vue'
import { httpService, type HttpError } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import type { Subscriptions_MarketCountriesResponseDados } from '../contracts/Subscriptions_MarketCountriesResponse'
import type { Subscriptions_MarketCountryRow } from '../contracts/Subscriptions_MarketCountryRow'
// Import side-effect: registers the contracts with the responseContract registry.
import '../contracts/Subscriptions_MarketCountriesResponse'
import '../contracts/Subscriptions_MarketCountryRow'

/**
 * useMarketCountries — SOLE network owner for GET subscriptions/admin/market-countries.
 *
 * D6: the staff market panels (PlanMarketsPanel / CreditPackMarketsPanel) must
 * never hardcode a country or currency list. The country list comes from the
 * locations-service (via the backend endpoint), annotated with the billing
 * market verdict (billing_ready + currency from MarketPolicy).
 *
 * Module-scope singleton state with an idempotent load() (cached after the
 * first successful fetch; concurrent callers share the in-flight promise).
 * Call `_reset()` in afterEach for test isolation.
 */

// ── Module-scope singleton state ────────────────────────────────────────────

const countries = ref<Subscriptions_MarketCountryRow[]>([])
let loaded = false
let loadPromise: Promise<void> | null = null

/**
 * billingReadyCountries — select-list options for the billing-ready countries
 * only ({ID: code, Name: "CODE — Name"}). These are the ONLY countries the
 * staff market panels may offer.
 */
const billingReadyCountries = computed(() =>
  countries.value
    .filter((c) => c.billing_ready === true)
    .map((c) => ({ ID: c.code ?? '', Name: `${c.code ?? ''} — ${c.name ?? ''}` })),
)

/**
 * marketCurrencies — unique, sorted currency codes of the billing-ready
 * countries (today BRL/CLP/USD). Drives the market panels' currency options.
 *
 * Reads `currencies` (every currency a country may be billed in), falling back
 * to the `currency` primary for a backend that predates that field.
 */
const marketCurrencies = computed<string[]>(() => {
  const seen = new Set<string>()
  for (const c of countries.value) {
    if (c.billing_ready !== true) continue
    for (const cur of currenciesOf(c)) seen.add(cur)
  }
  return Array.from(seen).sort()
})

// ── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Every currency a country row may be billed in.
 *
 * A country stopped having exactly one currency on 2026-09-08 (CL/EC gained BRL
 * for the ASAAS card route), so `currency` became the PRIMARY rather than the
 * only one. The fallback keeps a frontend deployed ahead of its backend honest:
 * an older API sends only `currency`, and reading `currencies` alone would make
 * every country answer "no currencies" — emptying the panels' options rather
 * than failing visibly.
 */
function currenciesOf(c: Subscriptions_MarketCountryRow): string[] {
  if (Array.isArray(c.currencies) && c.currencies.length > 0) {
    return c.currencies.filter((x) => typeof x === 'string' && x !== '')
  }
  return c.currency != null && c.currency !== '' ? [c.currency] : []
}

function sistemaUrl(path: string): string {
  return `${config.api.sistemaHost}/${path}`
}

// ── Public composable ────────────────────────────────────────────────────────

export function useMarketCountries() {
  /**
   * load — GET subscriptions/admin/market-countries (idempotent).
   *
   * Cached after the first successful fetch; a failed fetch is NOT cached so
   * a later call can retry. Concurrent callers share the in-flight promise.
   */
  async function load(): Promise<void> {
    if (loaded) return
    if (loadPromise) return loadPromise
    loadPromise = (async () => {
      try {
        const raw = await httpService.get(
          sistemaUrl('subscriptions/admin/market-countries'),
          { responseContract: 'Subscriptions_MarketCountriesResponse', skipErrorDisplay: false },
        )
        const resp = raw as { dados: Subscriptions_MarketCountriesResponseDados }
        countries.value = resp.dados.rows
        loaded = true
        log('useMarketCountries', 'load', 'Países de mercado carregados', {
          count: countries.value.length,
          billingReady: billingReadyCountries.value.length,
        })
      } catch (err) {
        log('useMarketCountries', 'load', 'Erro ao carregar países de mercado', {
          message: (err as Error)?.message,
          status: (err as HttpError)?.status,
        })
        throw err
      } finally {
        loadPromise = null
      }
    })()
    return loadPromise
  }

  /**
   * _reset — wipes module-scope state back to initial values.
   * Call in afterEach to prevent cross-test leakage.
   */
  function _reset(): void {
    countries.value = []
    loaded = false
    loadPromise = null
  }

  /**
   * countriesForCurrency — select-list options ({ID: code, Name}) for the
   * billing-ready countries that may be billed in `currency`. The market
   * panels need this per-currency cut; filtering `billingReadyCountries` is
   * impossible (its options carry no currency field).
   *
   * MEMBERSHIP, not equality against the primary: Chile is a CLP market that
   * may also be billed in BRL, so an equality test would hide it from the
   * country options of a BRL price row — the exact row the multi-currency
   * change exists to let staff author.
   */
  function countriesForCurrency(currency: string): Array<{ ID: string; Name: string }> {
    return countries.value
      .filter((c) => c.billing_ready === true && currenciesOf(c).includes(currency))
      .map((c) => ({ ID: c.code ?? '', Name: `${c.code ?? ''} — ${c.name ?? ''}` }))
  }

  return {
    // reactive state
    countries,
    billingReadyCountries,
    marketCurrencies,
    // methods
    load,
    countriesForCurrency,
    _reset,
  }
}
